import {ApplicationInitStatus, Injectable, OnInit} from '@angular/core';
import {AuthData, UserSettings} from '../services/common-datatypes';
import {HttpClient, HttpErrorResponse, HttpParams, HttpResponse} from '@angular/common/http';
import {map, tap} from 'rxjs/operators';
import {BehaviorSubject, Observable, of, Subject} from 'rxjs';
import {StateStorageService} from '../services/state-storage.service';
import {RestDataService} from '../services/rest-data.service';
import {NavController} from '@ionic/angular';
import {CommonFunctionsService} from "../services/common-functions.service";

export class AuthResult {
    success: boolean = false;
    errorCode?: number;
    errorMessage?: string;
}

@Injectable({
    providedIn: 'root'
})
export class NonceAuthService {
    private knownServerState: boolean;
    private apiEndPoint: string = '';
    private readonly authData: AuthData;

    private fullNonces: BehaviorSubject<string>[];
    private emptyNonces: Subject<string>[];

    constructor(private appInit: ApplicationInitStatus,
                private commonFunctions: CommonFunctionsService,
                private http: HttpClient,
                private appState: StateStorageService,
                private dataService: RestDataService,
                private navController: NavController) {

        this.knownServerState = false;
        this.authData = new AuthData();
        this.fullNonces = [];
        this.emptyNonces = [];
        appInit.donePromise.then(() => {
            this.appState.authData = this.authData;
            this.apiEndPoint = this.appState.apiEndPoint;
            this.fullNonces.push(new BehaviorSubject(this.appState.storedNonce));
        });
    }

    public testLogin(username: string): Observable<boolean> {
        return this.doBackendCall(username, '', '/testlogin', 'post', true)
            .pipe(map(authResult => authResult.success));
    }

    public login(username: string, password: string): Observable<AuthResult> {
        return this.doBackendCall(username, password, '/login');
    }

    public doBackendCall(username: string, password: string, url: string, method?: string, silent?: boolean): Observable<AuthResult> {
        const loginSubject = new Subject<AuthResult>();
        this.callBackend(username, password, url, method, silent)
            .subscribe(() => {
                    loginSubject.next({'success': this.authData.loggedIn});
                    loginSubject.complete();
                },
                (loginError) => {
                    loginSubject.next({
                        'success': false,
                        'errorCode': loginError.error ? loginError.error.errorCode : 'unknown error',
                        'errorMessage': loginError.error ? loginError.error.error : 'unknown error'
                    });
                    loginSubject.complete();
                }
            );
        return loginSubject.asObservable();
    }

    public doLogout(): Promise<any> {
        return this.callBackend('logout', 'logout', '/logout').toPromise()
            .then(() => this.unsetAuthData(false));
    }

    public isLoggedOut(): void {
        this.unsetAuthData(true); // setting to false will prevent logins
        this.navController.navigateRoot('/home');
    }

    public isLoggedIn(): Observable<boolean> {
        if (!this.knownServerState) {
            return this.testLogin(this.authData.userName);
        }
        return of(this.authData.loggedIn);
    }

    public getAuthToken(): Observable<string> {
        // console.log('nonce-auth token pool before using: ' + this.fullNonces.length);
        if (0 < this.fullNonces.length) {
            const nonce = this.fullNonces.shift();
            return nonce!;
        } else {
            this.commonFunctions.showError('Request delayed', 'waiting for auth token');
            if ((!this.knownServerState) || (!this.authData.loggedIn)) {
                return of('');
            }
            const nonceToFill = new Subject<string>();
            this.emptyNonces.push(nonceToFill);
            return nonceToFill.asObservable();
        }
    }

    public setAuthToken(tokens: string | null): void {
        if (tokens) {
            const tokenArray: string[] = tokens.split(',');
            // console.log('nonce-auth got tokens: ' + tokenArray.length);
            if (tokenArray.length > 1) {
                // if we get more than one token the session name did change, so refresh all stored ones
                this.appState.storedNonce = <string>tokenArray.pop();
                this.fullNonces = [];
            }
            for (const item of tokenArray) {
                if (!this.knownServerState) this.clearEmptyNonces();
                if (0 < this.emptyNonces.length) {
                    const nonceToFill = this.emptyNonces.shift();
                    nonceToFill!.next(item);
                } else {
                    this.fullNonces.push(new BehaviorSubject(item));
                }
            }
        }
    }

    private clearEmptyNonces() {
        this.emptyNonces.forEach(item => {
            item.error('logout');
        });
        this.emptyNonces = [];
    }

    private setAuthData(user: AuthData) {
        if (!user.loggedIn) return this.unsetAuthData();
        this.knownServerState = true;
        this.authData.loggedIn = user.loggedIn;
        this.authData.userName = user.userName;
        this.authData.realName = user.realName ?? '';
        this.authData.email = user.email ?? '';
        this.authData.role = user.role;
        this.authData.department = user.department ?? '';
        this.appState.authData = this.authData;

        // get admin settings after authentication
        this.dataService.getUserSettings()
            .then((data: UserSettings) => {
                if (!data) return;
                this.appState.settings = data;
                const settingsObject = new UserSettings(this.appState.defaultSearchSort);
                settingsObject.updateFromJson(data);
                this.appState.searchData = settingsObject.defaultSearch;
            });
    }

    public unsetAuthData(keep = true) {
        let lastNonce: BehaviorSubject<string> | undefined;
        if (0 < this.fullNonces.length) lastNonce = this.fullNonces.pop();

        this.knownServerState = true;
        this.authData.loggedIn = false;
        this.authData.userName = '';
        this.authData.realName = '';
        this.authData.email = '';
        this.authData.role = 0;
        this.authData.department = '';
        this.appState.authData = this.authData;
        this.fullNonces.length = 0;

        // keep only last nonce
        if (keep && lastNonce) this.fullNonces.push(lastNonce);
        if (!keep) this.appState.storedNonce = '';
        // dismiss all open nonce requests
        this.clearEmptyNonces();
    }

    private resetAuthData() {
        this.unsetAuthData();
        this.knownServerState = false;
    }

    public callBackend(username: string, password: string, apiPoint: string, method: string = 'post', silent: boolean = false): Observable<HttpResponse<any>> {
        let params = new HttpParams(); //.set('isLogin', 'true');
        if (silent) params = params.set('silentError', 'true');
        return this.http.request(method, this.apiEndPoint + apiPoint,
            {
                body: {username: username, password: password},
                withCredentials: true,
                reportProgress: true,
                params: params
            })
            .pipe(tap(
                (res: any) => {
                    if (res) {
                        this.setAuthData(res);
                    } else this.unsetAuthData();
                },
                (err: HttpErrorResponse) => {
                    if (err.status === 401) {
                        this.unsetAuthData();
                    } else this.resetAuthData();
                }
            ));
    }

}
